// Everything Dispatch derives from an order, in one place: the display shape,
// age/urgency, what a dispatcher can do with it, filters, sorting and KPIs.

import { zoneOrDefault } from '../../utils/restaurantTime'

export const MIN = 60 * 1000
export const HOUR = 60 * MIN
export const DAY = 24 * HOUR

/** Age thresholds, in minutes since the order was placed. */
export const AGE_WARN_MIN = 15
export const AGE_LATE_MIN = 45
export const STALE_MIN = 24 * 60

export const REFRESH_MS = 15000

export const STATUS_META = {
  PAYMENT_PENDING: { label: 'Awaiting payment', tone: 'muted', step: 0 },
  SCHEDULED: { label: 'Scheduled', tone: 'muted', step: 1 },
  PENDING: { label: 'New', tone: 'new', step: 2 },
  ACCEPTED: { label: 'Accepted', tone: 'accepted', step: 3 },
  PREPARED: { label: 'Prepared', tone: 'prepared', step: 4 },
  ASSIGNED: { label: 'Assigned', tone: 'assigned', step: 5 },
  EN_ROUTE_PICKUP: { label: 'En route to pickup', tone: 'transit', step: 6 },
  ARRIVED_PICKUP: { label: 'At restaurant', tone: 'transit', step: 7 },
  PICKED: { label: 'Picked up', tone: 'transit', step: 8 },
  EN_ROUTE_DROPOFF: { label: 'En route to drop', tone: 'transit', step: 9 },
  ARRIVED_DROPOFF: { label: 'At customer', tone: 'transit', step: 10 },
  DELIVERED: { label: 'Delivered', tone: 'delivered', step: 11 },
  // A pickup order the customer has collected. The server leaves it at PICKED
  // for good — the restaurant app files it under Completed — so it's finished.
  COLLECTED: { label: 'Collected', tone: 'delivered', step: 11 },
  CANCELLED: { label: 'Cancelled', tone: 'cancelled', step: 12 },
}
export const statusMeta = s => STATUS_META[s] || { label: s || 'Unknown', tone: 'muted', step: 99 }

// Same set the server's dispatchOrderCounts.inTransit sums, so tab counts agree.
export const IN_TRANSIT = ['ASSIGNED', 'EN_ROUTE_PICKUP', 'ARRIVED_PICKUP', 'PICKED', 'EN_ROUTE_DROPOFF', 'ARRIVED_DROPOFF']

export const STATUS_TABS = [
  { key: 'ALL', label: 'All', server: 'all', history: true },
  { key: 'ACTIVE', label: 'Active', server: 'active' },
  { key: 'PENDING', label: 'New', server: 'pending', tone: 'new' },
  { key: 'ACCEPTED', label: 'Accepted', server: 'accepted', tone: 'accepted' },
  { key: 'PREPARED', label: 'Prepared', server: 'prepared', tone: 'prepared' },
  { key: 'ASSIGNED', label: 'Assigned', server: 'assigned', tone: 'assigned' },
  { key: 'IN_TRANSIT', label: 'In transit', server: 'inTransit', tone: 'transit' },
  { key: 'DELIVERED', label: 'Delivered', server: 'delivered', tone: 'delivered', history: true },
  { key: 'CANCELLED', label: 'Cancelled', server: 'cancelled', tone: 'cancelled', history: true },
]

/**
 * Who carries a delivery — one per order; one still waiting for a driver is
 * RushMyFood's to dispatch. Dispatch lists deliveries only (a pickup, dine-in
 * or counter ticket has nothing to dispatch), so those have no tab.
 */
export const FULFILMENT_TABS = [
  { key: 'ALL', label: 'All types' },
  { key: 'RESTAURANT', label: 'Restaurant delivery' },
  { key: 'RUSHMYFOOD', label: 'RushMyFood delivery' },
  { key: 'FIRST_DELIVERY', label: 'First Delivery' },
]

export const CHANNELS = {
  UNASSIGNED: 'Unassigned',
  RUSHMYFOOD: 'RushMyFood',
  FIRST_DELIVERY: 'FirstDelivery',
  STAFF: 'Staff',
  // Only on a restaurant's board: a delivery from the old RushMyFood app (externalViews.js).
  RUSHMYFOOD_OLD: 'RushMyFood (old)',
}

export const COLUMNS = [
  { id: 'age', label: 'Age' },
  { id: 'order', label: 'Order', locked: true },
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'status', label: 'Status' },
  { id: 'eta', label: 'ETA' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'customer', label: 'Customer' },
  { id: 'amount', label: 'Amount' },
]
/** Columns the split map view drops so the table still fits beside it. */
export const SPLIT_HIDDEN = ['eta', 'customer']

// ─── Formatting ──────────────────────────────────────────────────────────

/** ISO string, epoch number, or epoch-in-a-string → ms, or null. */
export const ms = v => {
  if (v == null || v === '') return null
  if (typeof v === 'number') return v
  const t = /^\d+$/.test(String(v)) ? Number(v) : new Date(v).getTime()
  return Number.isFinite(t) ? t : null
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
export const money = n => usd.format(Number(n) || 0)

// `tz` is the order's restaurant zone (view.restaurant.timezone): times read on
// the restaurant's clock, "12:30 PM CST". Without one (filter labels, partner
// orders) they read on this browser's clock, as before.
const zoneOpts = tz => (tz ? { timeZone: tz } : {})
export const timeShort = (ts, tz) =>
  new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(tz ? { timeZone: tz, timeZoneName: 'short' } : {}) })
export const dateShort = (ts, tz) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...zoneOpts(tz) })
export const fullStamp = (ts, tz) =>
  `${new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...zoneOpts(tz) })} · ${timeShort(ts, tz)}`
export const isSameDay = (a, b, tz) =>
  tz
    ? new Date(a).toLocaleDateString('en-US', { timeZone: tz }) === new Date(b).toLocaleDateString('en-US', { timeZone: tz })
    : new Date(a).toDateString() === new Date(b).toDateString()
export const startOfDay = ts => {
  const d = new Date(ts)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}
/** A clock time today, a date otherwise — never both, so it fits one line. */
export const smartTime = (ts, now, tz) => (isSameDay(ts, now, tz) ? timeShort(ts, tz) : dateShort(ts, tz))

/** Compact span: "4 min", "1h 20m", "6 days", "3 wk". */
export const span = msv => {
  const m = Math.max(0, Math.floor(msv / MIN))
  if (m < 1) return 'now'
  if (m < 60) return `${m} min`
  if (msv < DAY) {
    const h = Math.floor(m / 60)
    const rest = m % 60
    return rest ? `${h}h ${rest}m` : `${h}h`
  }
  const d = Math.floor(msv / DAY)
  if (d < 14) return `${d} day${d === 1 ? '' : 's'}`
  return `${Math.floor(d / 7)} wk`
}
export const ago = (ts, now) => (span(now - ts) === 'now' ? 'just now' : `${span(now - ts)} ago`)
export const relative = (ts, now) => {
  if (Math.abs(ts - now) < MIN) return 'now'
  return ts > now ? `in ${span(ts - now)}` : `${span(now - ts)} ago`
}

export const haversineMiles = (a, b) => {
  if (!a || !b) return null
  const R = 3958.8
  const rad = d => (d * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

export const initials = name =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('') || '?'

/** "15 Wright St, Stoneham, MA 02180, USA" → "15 Wright St, Stoneham". */
export const shortAddress = full => {
  const parts = String(full || '').split(',').map(s => s.trim()).filter(Boolean)
  return parts.slice(0, 2).join(', ') || String(full || '')
}

// ─── Order → display shape ───────────────────────────────────────────────

const coords = c => (Array.isArray(c) && c.length >= 2 && c[0] != null && c[1] != null && !isNaN(c[0]) && !isNaN(c[1])
  ? { lat: parseFloat(c[1]), lng: parseFloat(c[0]) }
  : null)

const HISTORY_FIELDS = [
  ['PENDING', 'createdAt'],
  ['ACCEPTED', 'acceptedAt'],
  ['PREPARED', 'preparedAt'],
  ['ASSIGNED', 'assignedAt'],
  ['EN_ROUTE_PICKUP', 'enRoutePickupAt'],
  ['ARRIVED_PICKUP', 'arrivedPickupAt'],
  ['PICKED', 'pickedAt'],
  ['EN_ROUTE_DROPOFF', 'enRouteDropoffAt'],
  ['ARRIVED_DROPOFF', 'arrivedDropoffAt'],
  ['DELIVERED', 'deliveredAt'],
  ['CANCELLED', 'cancelledAt'],
]

/**
 * One shape for every order on the board, built from the GraphQL order.
 * ETA rules are the ones the old Pickup/Dropoff columns used.
 */
export function toView(o) {
  const r = o.restaurant || {}
  const da = o.deliveryAddress || null
  const orderType = o.orderType || 'DELIVERY'
  const isPos = orderType.startsWith('POS_')
  // isPickedUp is the "customer collects it" flag, not "a driver picked it up".
  // Same rule as the server's dispatchOrderCounts: only a DELIVERY order is one.
  const type = o.isPickedUp || isPos || orderType === 'PICKUP' ? 'PICKUP' : 'DELIVERY'
  const typeLabel =
    orderType === 'POS_DINE_IN' ? 'Dine-in' : orderType === 'POS_COUNTER' ? 'Counter' : type === 'PICKUP' ? 'Pickup' : 'Delivery'
  const placedAt = ms(o.createdAt) || 0
  // The restaurant's "Customer picked up" button moves a pickup order to PICKED
  // and nothing moves it on from there: for a pickup, PICKED means done.
  const collected = o.orderStatus === 'PICKED' && !!o.isPickedUp

  const rider = o.rider || null
  const driver = rider
    ? {
        id: rider._id,
        name: rider.name || rider.username || 'Driver',
        phone: rider.phone || null,
        photo: rider.pictureUrl || null,
        kind: rider.driverType === 'RESTAURANT' ? 'STAFF' : 'RUSHMYFOOD',
        location: coords(rider.location?.coordinates),
      }
    : o.fdDriverName
      ? {
          id: null,
          name: o.fdDriverName,
          phone: o.fdDriverPhone || null,
          photo: o.fdDriverPhoto || null,
          kind: 'FIRST_DELIVERY',
          transport: o.fdDriverTransport || null,
        }
      : null
  const channel = o.firstDeliveryOrderId
    ? 'FIRST_DELIVERY'
    : rider?.driverType === 'RESTAURANT' || o.isSelfDelivery
      ? 'STAFF'
      : rider
        ? 'RUSHMYFOOD'
        : 'UNASSIGNED'

  const fulfilment =
    orderType === 'POS_DINE_IN' ? 'DINE_IN'
      : orderType === 'POS_COUNTER' ? 'COUNTER'
        : type === 'PICKUP' ? 'PICKUP'
          : channel === 'FIRST_DELIVERY' ? 'FIRST_DELIVERY'
            : channel === 'STAFF' ? 'RESTAURANT'
              : 'RUSHMYFOOD'

  const customer = o.user && (o.user.name || o.user.phone)
    ? { name: o.user.name || o.user.phone, phone: o.user.phone || null, email: o.user.email || null }
    : o.posCustomerName || o.posCustomerPhone
      ? { name: o.posCustomerName || 'Walk-in', phone: o.posCustomerPhone || null }
      : null

  const address =
    type === 'DELIVERY' && da && da.deliveryAddress
      ? {
          full: da.deliveryAddress,
          short: shortAddress(da.deliveryAddress),
          details: [
            da.apartment && `Apt ${da.apartment}`,
            da.buildingName,
            da.entryCode && `Entry code ${da.entryCode}`,
            da.dropoffOption,
            da.details,
          ].filter(Boolean).join(' · '),
          location: da.latitude && da.longitude ? { lat: parseFloat(da.latitude), lng: parseFloat(da.longitude) } : null,
        }
      : null

  // Pickup: done once picked (or, for a customer pickup, once it's ready).
  const pickedAt = ms(o.pickedAt)
  const preparedAt = ms(o.preparedAt)
  const prepMin = parseInt(o.preparationTime || r.preparationTime || 0, 10)
  let pickup = null
  if (type === 'PICKUP' && preparedAt) pickup = { at: preparedAt, done: true }
  else if (pickedAt) pickup = { at: pickedAt, done: true }
  else if (ms(o.fdPickupEta)) pickup = { at: ms(o.fdPickupEta), done: false }
  else if (preparedAt) pickup = { at: preparedAt, done: false }
  else if (prepMin && placedAt) pickup = { at: placedAt + prepMin * MIN, done: false }

  const deliveredAt = ms(o.deliveredAt)
  const delMin = parseInt(r.deliveryTime || 0, 10)
  let dropoff = null
  if (type === 'DELIVERY') {
    if (deliveredAt) dropoff = { at: deliveredAt, done: true }
    else if (ms(o.fdDropoffEta)) dropoff = { at: ms(o.fdDropoffEta), done: false }
    else if (ms(o.expectedTime)) dropoff = { at: ms(o.expectedTime), done: false }
    else if (delMin && placedAt) dropoff = { at: placedAt + delMin * MIN, done: false }
  }

  const history = HISTORY_FIELDS
    .map(([status, field]) => ({ status, at: ms(o[field]) }))
    .filter(h => h.at)
    .sort((a, b) => a.at - b.at)

  return {
    raw: o,
    id: o._id,
    orderNo: o.orderId || String(o._id),
    status: collected ? 'COLLECTED' : o.orderStatus,
    type,
    typeLabel,
    // Typed in by the restaurant (New delivery); the customer paid it directly.
    ...(o.orderSource === 'MANUAL' ? { sourceLabel: 'Manual', manual: true } : {}),
    fulfilment,
    channel,
    driver,
    awaitingCourier: !!o.firstDeliveryOrderId && !o.fdDriverName && !rider,
    restaurant: {
      id: r._id || null,
      name: r.name || 'Unknown restaurant',
      image: r.image || null,
      address: r.address || null,
      phone: r.phone || null,
      location: coords(r.location?.coordinates),
      zoneId: r.zone?._id || null,
      // Every time on this order reads on the restaurant's clock.
      timezone: zoneOrDefault(r.timezone),
    },
    customer,
    address,
    placedAt,
    pickup,
    dropoff,
    deliveredAt,
    collectedAt: collected ? pickedAt : null,
    cancelledAt: ms(o.cancelledAt),
    scheduledFor: o.orderStatus === 'SCHEDULED' ? ms(o.expectedTime) : null,
    total: Number(o.paidAmount) || 0,
    tip: Number(o.tipping) || 0,
    history,
  }
}

// ─── Questions a dispatcher asks of an order ─────────────────────────────

export const isDone = v => v.status === 'DELIVERED' || v.status === 'COLLECTED' || v.status === 'CANCELLED'
export const isActive = v => !isDone(v) && v.status !== 'PAYMENT_PENDING'
export const isInTransit = v => IN_TRANSIT.includes(v.status)
/** Driver physically moving — ASSIGNED counts as in transit for the tab, but hasn't set off. */
export const isMoving = v => isInTransit(v) && v.status !== 'ASSIGNED'

export function ageLevel(v, now) {
  // No usable placed time: show it neutrally rather than as 50 years stale.
  if (isDone(v) || v.status === 'PAYMENT_PENDING' || !v.placedAt) return 'done'
  if (v.status === 'SCHEDULED') return 'scheduled'
  const m = (now - v.placedAt) / MIN
  if (m >= STALE_MIN) return 'stale'
  if (m >= AGE_LATE_MIN) return 'late'
  if (m >= AGE_WARN_MIN) return 'warn'
  return 'fresh'
}
export const isStale = (v, now) => ageLevel(v, now) === 'stale'

/** When the customer was promised the food — dropoff for delivery, ready time for pickup. */
export const dueAt = v => v.dropoff?.at ?? v.pickup?.at ?? v.placedAt + AGE_LATE_MIN * MIN
/** Open past its due time, however old — stale orders count too. */
export const isLate = (v, now) =>
  isActive(v) && v.status !== 'SCHEDULED' && !!v.placedAt && now > dueAt(v)

/** A delivery with nobody carrying it and no courier network already looking — however old. */
export const needsDriver = v =>
  isActive(v) &&
  v.status !== 'SCHEDULED' &&
  v.type === 'DELIVERY' &&
  !v.driver &&
  !v.awaitingCourier &&
  !isInTransit(v)

// An `external` view (an old-app or email order, see externalViews.js) is
// read-only: none of our order mutations accept its id.

/** Same rule the old drawer used to show the rider picker. */
export const canAssign = v => !v.external && isActive(v) && v.type === 'DELIVERY' && !v.driver && !isInTransit(v)

/** Same rule as before: live, a delivery, nobody on it, not already with First Delivery. */
export const canSendToFirstDelivery = v =>
  !v.external && isActive(v) && v.type === 'DELIVERY' && !v.raw.rider && !v.raw.firstDeliveryOrderId

/** RushMyFood is offering a self-delivery order to its riders, or First Delivery has it. */
export const findingRushmyfoodDriver = v =>
  !!v.raw.isSelfDelivery && !v.raw.rider && ['TIER_2', 'TIER_3'].includes(v.raw.dispatchTier)

/**
 * A restaurant's own board: a self-delivery order, accepted, nobody on it and
 * RushMyFood not already looking — the server checks the same.
 */
export const canSendToRushmyfood = v =>
  !v.external && isActive(v) && v.type === 'DELIVERY' && !!v.raw.isSelfDelivery &&
  ['ACCEPTED', 'PREPARED'].includes(v.status) && !v.raw.rider && !v.raw.firstDeliveryOrderId &&
  !findingRushmyfoodDriver(v)

/** Status moves the drawer offered before: accept, mark prepared, mark delivered. */
export const NEXT_STEP = {
  PENDING: { label: 'Accept order', to: 'ACCEPTED' },
  ACCEPTED: { label: 'Mark prepared', to: 'PREPARED' },
  PICKED: { label: 'Mark delivered', to: 'DELIVERED' },
}
export const canMoveTo = (v, to) =>
  !v.external && (to === 'CANCELLED' ? isActive(v) : NEXT_STEP[v.status]?.to === to)

/**
 * Lower rank = nearer the top. Late orders nobody is carrying beat everything;
 * stale tickets sink below live work; finished orders come last.
 */
export function urgencyRank(v, now) {
  if (!isActive(v)) return 6
  if (isStale(v, now)) return 5
  const late = isLate(v, now)
  const nd = needsDriver(v, now)
  if (late && nd) return 0
  if (late) return 1
  if (v.status === 'PENDING') return 2
  if (nd) return 3
  return 4
}

// ─── Filtering, counting, sorting ────────────────────────────────────────

export function matchesTab(v, tab) {
  if (v.status === 'PAYMENT_PENDING') return false
  switch (tab) {
    case 'ACTIVE':
      return isActive(v)
    case 'IN_TRANSIT':
      return isInTransit(v)
    case 'DELIVERED':
      return v.status === 'DELIVERED' || v.status === 'COLLECTED'
    case 'ALL':
      return true
    // A restaurant's board: its orders from the old app and from partner emails, whatever their status.
    case 'OLD_APP':
      return v.external === 'EHUNGRY'
    case 'EMAIL':
      return v.external === 'EMAIL'
    default:
      return v.status === tab
  }
}

export function dateBounds(range, now) {
  const today = startOfDay(now)
  switch (range.preset) {
    case 'today':
      return [today, Infinity]
    case 'yesterday':
      return [today - DAY, today]
    case '7d':
      return [today - 6 * DAY, Infinity]
    case '30d':
      return [today - 29 * DAY, Infinity]
    case 'custom': {
      const from = range.from ? startOfDay(new Date(`${range.from}T00:00`).getTime()) : -Infinity
      const to = range.to ? startOfDay(new Date(`${range.to}T00:00`).getTime()) + DAY : Infinity
      return [from, to]
    }
    default:
      return null
  }
}

/** Same fields the old search looked through. */
export function matchesSearch(v, q) {
  if (!q) return true
  const needle = q.toLowerCase().replace(/^#/, '')
  const o = v.raw
  return [
    o.orderId,
    o.restaurant?.name,
    o.deliveryAddress?.deliveryAddress,
    o.user?.name,
    o.user?.phone,
    o.user?.email,
    o.posCustomerName,
    o.posCustomerPhone,
    o.rider?.name,
    o.rider?.phone,
    o.fdDriverName,
    o.fdDriverPhone,
    o.paymentMethod,
    o.orderStatus,
    statusMeta(v.status).label,
    // The view's own fields, so orders shaped differently (externalViews.js) are found too.
    v.orderNo,
    v.restaurant.name,
    v.customer?.name,
    v.customer?.phone,
    v.address?.full,
    v.driver?.name,
    v.driver?.phone,
  ].some(h => h && String(h).toLowerCase().includes(needle))
}

export const EMPTY_FILTERS = {
  search: '',
  restaurants: [],
  channel: 'ALL',
  date: { preset: 'any' },
  hideStale: false,
  kpi: null,
  fulfilment: 'ALL',
}

export const filtersOn = f =>
  f.search.trim() !== '' || f.restaurants.length > 0 || f.channel !== 'ALL' || f.date.preset !== 'any' || f.hideStale || f.kpi != null ||
  f.fulfilment !== 'ALL'

export const tripMinutes = v => (v.deliveredAt && v.placedAt ? (v.deliveredAt - v.placedAt) / MIN : null)

export function avgTripOn(views, day) {
  const trips = views
    .filter(v => v.status === 'DELIVERED' && v.deliveredAt && isSameDay(v.deliveredAt, day))
    .map(tripMinutes)
    .filter(t => t != null && t > 0 && t < 24 * 60)
  return trips.length ? trips.reduce((a, b) => a + b, 0) / trips.length : null
}

export function matchesKpi(v, kpi, now, avgToday) {
  const deliveredToday = v.status === 'DELIVERED' && v.deliveredAt && isSameDay(v.deliveredAt, now)
  switch (kpi) {
    case 'needs_driver':
      return needsDriver(v, now)
    case 'late':
      return isLate(v, now)
    case 'in_transit':
      return isInTransit(v)
    case 'delivered_today':
      return !!deliveredToday
    case 'avg_delivery':
      return !!deliveredToday && avgToday != null && tripMinutes(v) > avgToday
    default:
      return true
  }
}

/** Everything except the status tab — tab counts come from this set. */
export function applyFilters(views, f, now) {
  const bounds = dateBounds(f.date, now)
  const avgToday = f.kpi === 'avg_delivery' ? avgTripOn(views, now) : null
  const q = f.search.trim()
  return views.filter(
    v =>
      (f.restaurants.length === 0 || f.restaurants.includes(v.restaurant.id)) &&
      (f.channel === 'ALL' || v.channel === f.channel) &&
      (f.fulfilment === 'ALL' || v.fulfilment === f.fulfilment) &&
      (!bounds || (v.placedAt >= bounds[0] && v.placedAt < bounds[1])) &&
      (!f.hideStale || !isStale(v, now)) &&
      matchesKpi(v, f.kpi, now, avgToday) &&
      matchesSearch(v, q),
  )
}

export function fulfilmentCounts(views) {
  const c = Object.fromEntries(FULFILMENT_TABS.map(t => [t.key, 0]))
  for (const v of views) {
    if (v.status === 'PAYMENT_PENDING') continue
    c.ALL++
    c[v.fulfilment]++
  }
  return c
}

export function localCounts(views) {
  const c = { ACTIVE: 0, PENDING: 0, ACCEPTED: 0, PREPARED: 0, ASSIGNED: 0, IN_TRANSIT: 0, DELIVERED: 0, CANCELLED: 0, ALL: 0, OLD_APP: 0, EMAIL: 0 }
  for (const v of views) {
    if (v.status === 'PAYMENT_PENDING') continue
    c.ALL++
    if (v.external === 'EHUNGRY') c.OLD_APP++
    if (v.external === 'EMAIL') c.EMAIL++
    if (isActive(v)) c.ACTIVE++
    if (isInTransit(v)) c.IN_TRANSIT++
    if (v.status === 'COLLECTED') c.DELIVERED++
    else if (c[v.status] != null && v.status !== 'IN_TRANSIT') c[v.status]++
  }
  return c
}

const SORT_VALUE = {
  age: (v, now) => now - v.placedAt,
  order: v => v.orderNo,
  restaurant: v => v.restaurant.name,
  status: v => statusMeta(v.status).step,
  eta: v => v.dropoff?.at ?? v.pickup?.at ?? Number.MAX_SAFE_INTEGER,
  delivery: v => `${CHANNELS[v.channel]} ${v.driver?.name || ''}`,
  customer: v => v.customer?.name || '~',
  amount: v => v.total,
}

export const FIRST_DIR = { urgency: 'asc', age: 'desc', order: 'desc', restaurant: 'asc', status: 'asc', eta: 'asc', delivery: 'asc', customer: 'asc', amount: 'desc' }

export function sortViews(views, sort, now) {
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...views].sort((a, b) => {
    if (sort.key === 'urgency') {
      const ra = urgencyRank(a, now)
      const rb = urgencyRank(b, now)
      if (ra !== rb) return ra - rb
      // Live work: oldest first. Stale and finished: most recent first.
      return ra <= 4 ? a.placedAt - b.placedAt : b.placedAt - a.placedAt
    }
    const f = SORT_VALUE[sort.key]
    const va = f(a, now)
    const vb = f(b, now)
    const cmp = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' })
    return cmp * dir || b.placedAt - a.placedAt
  })
}

// ─── KPIs ────────────────────────────────────────────────────────────────

export function computeKpis(views, now) {
  const active = views.filter(isActive)
  const nd = active.filter(v => needsDriver(v, now))
  const late = active.filter(v => isLate(v, now))
  // Both cards count every open order; the captions split out the ones under a day old.
  const fresh = xs => xs.filter(v => !isStale(v, now))
  const ndFresh = fresh(nd)
  const lateFresh = fresh(late)
  const transit = active.filter(isInTransit)
  const deliveredToday = views.filter(v => v.status === 'DELIVERED' && v.deliveredAt && isSameDay(v.deliveredAt, now))
  const recent = views
    .filter(v => v.status === 'DELIVERED' && v.deliveredAt && tripMinutes(v) > 0 && tripMinutes(v) < 24 * 60)
    .sort((a, b) => a.deliveredAt - b.deliveredAt)
    .slice(-12)
  const drivers = new Set(transit.map(v => v.driver && (v.driver.id || v.driver.name)).filter(Boolean))

  return {
    needsDriver: nd.length,
    needsDriverFresh: ndFresh.length,
    waitingOver15: ndFresh.filter(v => (now - v.placedAt) / MIN >= AGE_WARN_MIN).length,
    late: late.length,
    lateFresh: lateFresh.length,
    worstLateMs: lateFresh.length ? Math.max(...lateFresh.map(v => now - dueAt(v))) : 0,
    inTransit: transit.length,
    driversOnRoad: drivers.size,
    avgToday: avgTripOn(views, now),
    avgYesterday: avgTripOn(views, now - DAY),
    deliveredToday: deliveredToday.length,
    placedToday: views.filter(v => v.status !== 'PAYMENT_PENDING' && isSameDay(v.placedAt, now)).length,
    recentTrips: recent.map(v => ({ id: v.id, orderNo: v.orderNo, minutes: tripMinutes(v) })),
  }
}

// ─── CSV ─────────────────────────────────────────────────────────────────

const csvCell = x => {
  const s = x == null ? '' : String(x)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export function toCsv(views) {
  const header = ['Order', 'Status', 'Placed', 'Restaurant', 'Channel', 'Driver', 'Customer', 'Phone', 'Address', 'Total', 'Tip']
  const rows = views.map(v => [
    `#${v.orderNo}`,
    statusMeta(v.status).label,
    v.placedAt ? new Date(v.placedAt).toISOString() : '',
    v.restaurant.name,
    CHANNELS[v.channel],
    v.driver?.name || '',
    v.customer?.name || '',
    v.customer?.phone || '',
    v.address?.full || '',
    v.total.toFixed(2),
    v.tip.toFixed(2),
  ])
  return [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n')
}

export function downloadCsv(csv, filename) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
